/** A moment as the account's clock reads it: the day as yyyymmdd and the minutes since midnight. */
export type ZonedMoment = { day: number; minutes: number };

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timezone: string): Intl.DateTimeFormat {
    let found = formatters.get(timezone);
    if (!found) {
        found = new Intl.DateTimeFormat("en-US", {
            ...(timezone && { timeZone: timezone }),
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
        });
        formatters.set(timezone, found);
    }
    return found;
}

/** Without a time zone (or with one the browser does not know) the browser's own clock answers. */
export function zonedMoment(ts: number, timezone: string | undefined): ZonedMoment {
    try {
        const parts = formatter(timezone ?? "").formatToParts(new Date(ts));
        const part = (type: string) => Number(parts.find(p => p.type === type)?.value);
        const day = part("year") * 10000 + part("month") * 100 + part("day");
        const minutes = (part("hour") % 24) * 60 + part("minute");
        if (Number.isFinite(day) && Number.isFinite(minutes)) {
            return { day, minutes };
        }
    } catch {
        // fall through to the browser's clock
    }
    const date = new Date(ts);
    return { day: date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate(), minutes: date.getHours() * 60 + date.getMinutes() };
}

/** hhmm as the dashboard stores times of day (1030 = 10:30) to minutes since midnight. */
export function hhmmToMinutes(hhmm: number): number {
    return Math.floor(hhmm / 100) * 60 + (hhmm % 100);
}

/** Whether a moment is after hhmm on the given day, both read on the account's clock. */
export function isAfterTimeOnDay(ts: number, day: number, hhmm: number, timezone: string | undefined): boolean {
    const moment = zonedMoment(ts, timezone);
    return moment.day > day || (moment.day === day && moment.minutes > hhmmToMinutes(hhmm));
}
